import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/admin/api";
import { KpiCard, StatusBadge, Progress, money, moneyMap, Field } from "@/admin/ui";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Search, AlertTriangle, Clock } from "lucide-react";

const PROJECT_TYPES = ["Website", "Webshop", "School Website", "Hotel Website", "Portal", "Web App", "Mobile App", "Other"];
const STATUSES = ["Lead", "Confirmed", "Not Started", "In Progress", "Waiting for Customer", "On Hold", "Completed", "Cancelled"];
const CURRENCIES = ["GHS", "USD", "EUR", "CHF"];
const FILTERS = [
  { key: "", label: "All" }, { key: "active", label: "Active" }, { key: "waiting", label: "Waiting for customer" },
  { key: "overdue", label: "Overdue" }, { key: "payment_overdue", label: "Payment overdue" }, { key: "completed", label: "Completed" },
];

export default function Dashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");
  const [typeF, setTypeF] = useState("");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/dashboard");
      setData(data);
    } catch (e) { toast.error(apiError(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="text-mutedink">Loading dashboard…</div>;

  const k = data.kpis;
  let projects = data.projects;
  if (filter === "active") projects = projects.filter((p) => ["Confirmed", "Not Started", "In Progress", "Waiting for Customer", "On Hold"].includes(p.status));
  else if (filter === "completed") projects = projects.filter((p) => p.status === "Completed");
  else if (filter === "waiting") projects = projects.filter((p) => p.waiting_for_customer);
  else if (filter === "overdue") projects = projects.filter((p) => p.days_until_deadline != null && p.days_until_deadline < 0 && !["Completed", "Cancelled"].includes(p.status));
  else if (filter === "payment_overdue") projects = projects.filter((p) => p.payment_status === "Overdue");
  if (typeF) projects = projects.filter((p) => p.type === typeF);
  if (q) projects = projects.filter((p) => (p.name + p.customer_name + (p.contact_person || "")).toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-serif text-3xl font-700 text-navy">Projects</h1>
          <p className="text-sm text-mutedink">Every active engagement, from briefing to launch.</p>
        </div>
        <button onClick={() => setOpen(true)} data-testid="new-project-btn"
          className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 font-600 text-navy-dark transition-transform hover:-translate-y-0.5">
          <Plus className="h-4 w-4" /> New project
        </button>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <KpiCard label="Active projects" value={k.active_projects} />
        <KpiCard label="Waiting for customer" value={k.waiting_for_customer} tone="blue" />
        <KpiCard label="Deadlines this week" value={k.deadlines_this_week} tone="amber" />
        <KpiCard label="Overdue phases" value={k.overdue_phases} tone="red" />
        <KpiCard label="Payments due" value={k.payments_due} tone="amber" />
        <KpiCard label="Payments overdue" value={k.payments_overdue} tone="red" />
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <KpiCard label="Total contract value" value={moneyMap(k.total_contract_value)} />
        <KpiCard label="Total received" value={moneyMap(k.total_received)} tone="green" />
        <KpiCard label="Total outstanding" value={moneyMap(k.total_outstanding)} tone="amber" />
      </div>

      {/* Attention */}
      {data.attention?.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="attention-panel">
          <div className="mb-3 flex items-center gap-2 font-600 text-navy"><AlertTriangle className="h-4 w-4 text-amber-600" /> Attention required</div>
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            {data.attention.slice(0, 12).map((a, i) => (
              <div key={i} className="flex items-center gap-2 rounded-lg bg-white px-3 py-2 text-sm">
                <span className={`h-2 w-2 shrink-0 rounded-full ${a.level === "red" ? "bg-red-500" : "bg-amber-500"}`} />
                <span className="font-600 text-navy">{a.type}</span>
                <span className="text-mutedink">· {a.project}</span>
                <span className="ml-auto truncate text-xs text-mutedink">{a.detail}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map((f) => (
          <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`filter-${f.key || "all"}`}
            className={`rounded-full px-3.5 py-1.5 text-sm transition-colors ${filter === f.key ? "bg-navy text-white" : "bg-white text-navy border border-navy/15"}`}>
            {f.label}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <Select value={typeF || "all"} onValueChange={(v) => setTypeF(v === "all" ? "" : v)}>
            <SelectTrigger className="h-9 w-44 bg-white" data-testid="type-filter"><SelectValue placeholder="All types" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All types</SelectItem>{PROJECT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-mutedink" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="h-9 w-52 bg-white pl-9" data-testid="search-input" />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-navy/10 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-cream text-left text-xs uppercase tracking-wide text-mutedink">
              <tr>
                <th className="px-4 py-3">Customer / Project</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Owner</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 w-40">Progress</th>
                <th className="px-4 py-3">Next deadline</th>
                <th className="px-4 py-3">Payment</th>
                <th className="px-4 py-3 text-right">Outstanding</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-navy/5">
              {projects.map((p) => (
                <tr key={p.id} onClick={() => navigate(`/admin/projects/${p.id}`)} data-testid={`project-row-${p.id}`}
                  className="cursor-pointer hover:bg-cream/60">
                  <td className="px-4 py-3">
                    <div className="font-600 text-navy">{p.name}</div>
                    <div className="text-xs text-mutedink">{p.customer_name}{p.waiting_for_customer && <span className="ml-2 rounded bg-sky-100 px-1.5 py-0.5 text-[0.65rem] text-sky-700">waiting</span>}</div>
                  </td>
                  <td className="px-4 py-3 text-mutedink">{p.type}</td>
                  <td className="px-4 py-3 text-mutedink">{p.owner || "—"}</td>
                  <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
                  <td className="px-4 py-3"><Progress value={p.progress} /></td>
                  <td className="px-4 py-3">
                    {p.next_deadline ? (
                      <div className="flex items-center gap-1 text-xs">
                        <Clock className="h-3 w-3 text-mutedink" />
                        <span className={p.days_until_deadline < 0 ? "text-red-600 font-600" : p.days_until_deadline <= 7 ? "text-amber-600" : "text-navy"}>
                          {p.next_deadline} ({p.days_until_deadline}d)
                        </span>
                      </div>
                    ) : <span className="text-mutedink">—</span>}
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={p.payment_status} /></td>
                  <td className="px-4 py-3 text-right font-600 text-navy">{money(p.currency, p.outstanding)}</td>
                </tr>
              ))}
              {projects.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-mutedink">No projects match these filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <NewProjectDialog open={open} onClose={() => setOpen(false)} onCreated={(p) => { setOpen(false); navigate(`/admin/projects/${p.id}`); }} />
    </div>
  );
}

function NewProjectDialog({ open, onClose, onCreated }) {
  const [templates, setTemplates] = useState([]);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    customer_name: "", contact_person: "", email: "", phone: "", name: "", type: "Website",
    package: "", description: "", owner: "", currency: "GHS", contract_value: "", start_date: "",
    expected_launch: "", status: "Confirmed", template_id: "",
  });
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v?.target ? v.target.value : v }));

  useEffect(() => { if (open) api.get("/admin/templates").then((r) => setTemplates(r.data)); }, [open]);

  const create = async () => {
    if (!f.customer_name || !f.name) { toast.error("Customer and project name are required"); return; }
    setBusy(true);
    try {
      const payload = { ...f, contract_value: parseFloat(f.contract_value || 0) };
      const { data } = await api.post("/admin/projects", payload);
      toast.success("Project created with timeline");
      onCreated(data);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader><DialogTitle className="font-serif text-2xl text-navy">New project</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="Customer / company *"><Input value={f.customer_name} onChange={set("customer_name")} data-testid="np-customer" /></Field>
          <Field label="Contact person"><Input value={f.contact_person} onChange={set("contact_person")} /></Field>
          <Field label="Email"><Input value={f.email} onChange={set("email")} /></Field>
          <Field label="Phone / WhatsApp"><Input value={f.phone} onChange={set("phone")} /></Field>
          <Field label="Project name *"><Input value={f.name} onChange={set("name")} data-testid="np-name" /></Field>
          <Field label="Project type">
            <Select value={f.type} onValueChange={set("type")}><SelectTrigger data-testid="np-type"><SelectValue /></SelectTrigger>
              <SelectContent>{PROJECT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
          </Field>
          <Field label="Package"><Input value={f.package} onChange={set("package")} placeholder="e.g. School / Hotel" /></Field>
          <Field label="Project owner"><Input value={f.owner} onChange={set("owner")} placeholder="Virginie / Danstand" /></Field>
          <Field label="Template (generates phases)">
            <Select value={f.template_id || "none"} onValueChange={(v) => set("template_id")(v === "none" ? "" : v)}>
              <SelectTrigger data-testid="np-template"><SelectValue placeholder="Choose template" /></SelectTrigger>
              <SelectContent><SelectItem value="none">No template</SelectItem>{templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Currency">
            <Select value={f.currency} onValueChange={set("currency")}><SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
          </Field>
          <Field label="Contract value"><Input type="number" value={f.contract_value} onChange={set("contract_value")} data-testid="np-value" /></Field>
          <Field label="Status">
            <Select value={f.status} onValueChange={set("status")}><SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
          </Field>
          <Field label="Start date"><Input type="date" value={f.start_date} onChange={set("start_date")} data-testid="np-start" /></Field>
          <Field label="Expected launch"><Input type="date" value={f.expected_launch} onChange={set("expected_launch")} /></Field>
          <div className="md:col-span-2"><Field label="Description"><Textarea rows={3} value={f.description} onChange={set("description")} /></Field></div>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="rounded-full px-5 py-2 text-navy">Cancel</button>
          <button onClick={create} disabled={busy} data-testid="np-create" className="rounded-full bg-gold px-6 py-2 font-600 text-navy-dark disabled:opacity-60">{busy ? "Creating…" : "Create project"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
